/**
 * Move applier - transforms state
 */

import { getRandomAugmentationChoices } from "../augmentations/registry";
import type { MoveAction } from "../types/actions";
import type { Color, File, Rank, Square } from "../types/common";
import { FILES, RANKS } from "../types/common";
import type { ChessState } from "../types/game-state";
import type { Piece } from "../types/piece";
import {
  getMinorRevivalDelay,
  getRisingQueenDelay,
  hasAssassinAug,
  hasDualKnightPromotion,
  hasDualMinorPromotion,
  hasMinorRevivalAug,
  hasRisingQueenAug,
  hasSacrificeAug,
} from "../utils/augmentation-effects";
import {
  AUGMENTATION_CHOICES_PER_ROUND,
  AUGMENTATION_TRIGGER_TURNS,
} from "../utils/augmentation-schedule";
import { getPositionHash } from "../utils/fen";
import { isInCheck } from "../utils/moves";
import { squareToIndices } from "../utils/square";

/**
 * Update castling rights when king or rook moves
 */
function updateCastlingRights(
  state: ChessState,
  movedPiece: Piece,
  originalPosition: { file: string },
): void {
  const { type, color } = movedPiece;

  if (type === "KING") {
    if (color === "WHITE") {
      state.castlingRights.whiteKingside = false;
      state.castlingRights.whiteQueenside = false;
    } else {
      state.castlingRights.blackKingside = false;
      state.castlingRights.blackQueenside = false;
    }
  } else if (type === "ROOK") {
    if (color === "WHITE") {
      if (originalPosition.file === "a")
        state.castlingRights.whiteQueenside = false;
      if (originalPosition.file === "h")
        state.castlingRights.whiteKingside = false;
    } else {
      if (originalPosition.file === "a")
        state.castlingRights.blackQueenside = false;
      if (originalPosition.file === "h")
        state.castlingRights.blackKingside = false;
    }
  }
}

/**
 * Update castling rights when a rook is captured
 */
function updateCastlingRightsOnCapture(
  state: ChessState,
  capturedPiece: Piece,
): void {
  if (capturedPiece.type !== "ROOK") return;

  const { color, position } = capturedPiece;

  if (color === "WHITE") {
    if (position.file === "a" && position.rank === 1) {
      state.castlingRights.whiteQueenside = false;
    }
    if (position.file === "h" && position.rank === 1) {
      state.castlingRights.whiteKingside = false;
    }
  } else {
    if (position.file === "a" && position.rank === 8) {
      state.castlingRights.blackQueenside = false;
    }
    if (position.file === "h" && position.rank === 8) {
      state.castlingRights.blackKingside = false;
    }
  }
}

/**
 * Handle capture (normal, en passant, or Domination Rook en-route).
 * Returns true if any capture occurred.
 */
function handleCapture(state: ChessState, action: MoveAction): boolean {
  // En passant capture (mutually exclusive with standard capture at landing square)
  if (action.enPassantCaptureSquare && action.capturedPieceId) {
    const capturedIndex = state.pieces.findIndex(
      (p) => p.id === action.capturedPieceId,
    );
    if (capturedIndex !== -1) {
      const captureIndices = squareToIndices(action.enPassantCaptureSquare);
      state.board.squares[captureIndices.fileIndex]![
        captureIndices.rankIndex
      ]!.pieceId = null;
      state.pieces.splice(capturedIndex, 1);
    }
    return true;
  }

  let captured = false;

  // Domination Rook en-route capture: remove the passed-through piece and clear its square
  if (action.capturedEnRoutePieceId && action.capturedEnRouteSquare) {
    const enRouteIndex = state.pieces.findIndex(
      (p) => p.id === action.capturedEnRoutePieceId,
    );
    if (enRouteIndex !== -1) {
      const enRoutePiece = state.pieces[enRouteIndex];
      if (enRoutePiece) {
        updateCastlingRightsOnCapture(state, enRoutePiece);
      }
      state.pieces.splice(enRouteIndex, 1);
      const enRouteIndices = squareToIndices(action.capturedEnRouteSquare);
      state.board.squares[enRouteIndices.fileIndex]![
        enRouteIndices.rankIndex
      ]!.pieceId = null;
      captured = true;
    }
  }

  // Standard capture at landing square
  if (action.capturedPieceId) {
    const capturedIndex = state.pieces.findIndex(
      (p) => p.id === action.capturedPieceId,
    );
    if (capturedIndex !== -1) {
      const capturedPiece = state.pieces[capturedIndex];
      if (capturedPiece) {
        updateCastlingRightsOnCapture(state, capturedPiece);
      }
      state.pieces.splice(capturedIndex, 1);
      captured = true;
    }
  }

  return captured;
}

/**
 * Move the rook during castling
 */
function handleCastlingRookMove(
  state: ChessState,
  action: MoveAction,
  piece: Piece,
): void {
  if (
    action.moveType !== "CASTLE_KINGSIDE" &&
    action.moveType !== "CASTLE_QUEENSIDE"
  ) {
    return;
  }

  const rank = piece.color === "WHITE" ? 1 : 8;
  const rookFromFile: File = action.moveType === "CASTLE_KINGSIDE" ? "h" : "a";
  const rookToFile: File = action.moveType === "CASTLE_KINGSIDE" ? "f" : "d";

  const rookFromSquare: Square = { file: rookFromFile, rank: rank as Rank };
  const rookToSquare: Square = { file: rookToFile, rank: rank as Rank };

  const rook = state.pieces.find(
    (p) =>
      p.type === "ROOK" &&
      p.color === piece.color &&
      p.position.file === rookFromFile &&
      p.position.rank === rank,
  );

  if (rook) {
    const rookOldIndices = squareToIndices(rookFromSquare);
    state.board.squares[rookOldIndices.fileIndex]![
      rookOldIndices.rankIndex
    ]!.pieceId = null;

    rook.position = { ...rookToSquare };
    rook.hasMoved = true;

    const rookNewIndices = squareToIndices(rookToSquare);
    state.board.squares[rookNewIndices.fileIndex]![
      rookNewIndices.rankIndex
    ]!.pieceId = rook.id;
  }
}

/**
 * Set en passant target square for pawn double moves
 */
function updateEnPassantTarget(
  state: ChessState,
  action: MoveAction,
  piece: Piece,
): void {
  state.enPassantTarget = null;
  if (piece.type === "PAWN") {
    const rankDiff = Math.abs(action.to.rank - action.from.rank);
    if (rankDiff === 2) {
      const enPassantRank = piece.color === "WHITE" ? 3 : 6;
      state.enPassantTarget = {
        file: action.to.file,
        rank: enPassantRank as 3 | 6,
      };
    }
  }
}

/**
 * Track consecutive check counter for the moving player
 */
function trackConsecutiveChecks(state: ChessState, movingColor: Color): void {
  const colorKey = movingColor === "WHITE" ? "white" : "black";
  if (isInCheck(state, state.currentPlayer)) {
    state.consecutiveChecks = {
      ...state.consecutiveChecks,
      [colorKey]: state.consecutiveChecks[colorKey] + 1,
    };
  } else {
    state.consecutiveChecks = {
      ...state.consecutiveChecks,
      [colorKey]: 0,
    };
  }
}

/**
 * Place an additional piece on the nearest adjacent empty square to the promotion square.
 * Priority: same rank (file-1, file+1), then backward rank diagonals, then straight back.
 * Mutates state in place (called on deep-cloned state).
 */
function addPieceAdjacent(
  state: ChessState,
  color: Color,
  promotionSquare: Square,
  pieceType: Piece["type"],
  idSuffix: string,
  turnNumber?: number,
): void {
  const fi = FILES.indexOf(promotionSquare.file);
  const ri = RANKS.indexOf(promotionSquare.rank);
  // "backward" for white is decreasing rank index (toward rank 1); for black, increasing
  const backDir = color === "WHITE" ? -1 : 1;

  const candidates: [number, number][] = [
    [fi - 1, ri], // same rank, file-left
    [fi + 1, ri], // same rank, file-right
    [fi - 1, ri + backDir], // backward-left diagonal
    [fi, ri + backDir], // straight backward
    [fi + 1, ri + backDir], // backward-right diagonal
  ];

  for (const [cf, cr] of candidates) {
    if (cf < 0 || cf >= 8 || cr < 0 || cr >= 8) continue;

    const col = state.board.squares[cf];
    if (!col) continue;
    const sq = col[cr];
    if (!sq || sq.pieceId !== null) continue; // occupied

    const newPieceId = `${color.toLowerCase()}-${pieceType.toLowerCase()}-${idSuffix}-t${turnNumber ?? state.turnNumber}`;
    const newPiece: Piece = {
      id: newPieceId,
      type: pieceType,
      color,
      position: {
        file: FILES[cf] as File,
        rank: RANKS[cr] as Rank,
      },
      hasMoved: true,
    };

    state.pieces.push(newPiece);
    col[cr] = { ...sq, pieceId: newPieceId };
    break;
  }
}

/**
 * PN-006 Sacrifice: when a pawn is captured, instantly promote the most-advanced
 * remaining ally pawn to Queen (no promotion rank required).
 * Most-advanced = highest rank index for WHITE, lowest for BLACK.
 * Tie-breaking: alphabetically by file (a → h).
 * Mutates state in place (called on deep-cloned state).
 */
function applySacrificePromotion(
  state: ChessState,
  sacrificedColor: Color,
): void {
  if (!hasSacrificeAug(state, sacrificedColor)) return;

  const remainingPawns = state.pieces.filter(
    (p) => p.type === "PAWN" && p.color === sacrificedColor,
  );
  if (remainingPawns.length === 0) return;

  // Sort: most advanced first
  const sorted = [...remainingPawns].sort((a, b) => {
    const rankDiff =
      sacrificedColor === "WHITE"
        ? b.position.rank - a.position.rank // WHITE: descending rank
        : a.position.rank - b.position.rank; // BLACK: ascending rank
    if (rankDiff !== 0) return rankDiff;
    return a.position.file.localeCompare(b.position.file); // file tie-break
  });

  const target = sorted[0];
  if (!target) return;

  // Promote to Queen in-place (board square unchanged, piece stays at same position)
  target.type = "QUEEN";
}

/**
 * MN-001 Revival: canonical start squares for minor pieces by color and type.
 * WHITE: knights at b1/g1, bishops at c1/f1.
 * BLACK: knights at b8/g8, bishops at c8/f8.
 */
const MINOR_START_SQUARES: Record<
  Color,
  Record<"KNIGHT" | "BISHOP", Square[]>
> = {
  WHITE: {
    KNIGHT: [
      { file: "b", rank: 1 },
      { file: "g", rank: 1 },
    ],
    BISHOP: [
      { file: "c", rank: 1 },
      { file: "f", rank: 1 },
    ],
  },
  BLACK: {
    KNIGHT: [
      { file: "b", rank: 8 },
      { file: "g", rank: 8 },
    ],
    BISHOP: [
      { file: "c", rank: 8 },
      { file: "f", rank: 8 },
    ],
  },
} as const;

/**
 * Choose the canonical revival target square for a minor piece.
 * Picks the first canonical start square that is currently unoccupied;
 * falls back to the first if both are occupied (placement will use fallback logic).
 */
function getMinorRevivalTargetSquare(
  state: ChessState,
  color: Color,
  pieceType: "KNIGHT" | "BISHOP",
): Square {
  const squares = MINOR_START_SQUARES[color][pieceType];
  for (const sq of squares) {
    const fi = FILES.indexOf(sq.file as (typeof FILES)[number]);
    const ri = RANKS.indexOf(sq.rank as (typeof RANKS)[number]);
    if (fi >= 0 && ri >= 0 && !state.board.squares[fi]?.[ri]?.pieceId) {
      return sq;
    }
  }
  return squares[0]!; // Both occupied; placement will find nearest empty
}

/**
 * MN-001 Revival: place a revived minor piece on the board.
 * Tries targetSquare first; if occupied, scans adjacent squares for the nearest empty.
 * Mutates state in place (called on deep-cloned state).
 */
function applyMinorRevival(
  state: ChessState,
  color: Color,
  pieceType: Piece["type"],
  targetSquare: Square,
  turnNumber?: number,
): void {
  const fi = FILES.indexOf(targetSquare.file as (typeof FILES)[number]);
  const ri = RANKS.indexOf(targetSquare.rank as (typeof RANKS)[number]);

  if (fi < 0 || ri < 0) return;

  // Check if targetSquare is empty
  const sq = state.board.squares[fi]?.[ri];
  if (sq && !sq.pieceId) {
    // Place directly
    const newId = `${color.toLowerCase()}-${pieceType.toLowerCase()}-revival-t${turnNumber ?? state.turnNumber}`;
    const newPiece: Piece = {
      id: newId,
      type: pieceType,
      color,
      position: { file: targetSquare.file, rank: targetSquare.rank },
      hasMoved: false,
    };
    state.pieces.push(newPiece);
    state.board.squares[fi]![ri] = { ...sq, pieceId: newId };
  } else {
    // Target occupied — use addPieceAdjacent with idSuffix for fallback placement
    addPieceAdjacent(
      state,
      color,
      targetSquare,
      pieceType,
      "mn001",
      turnNumber,
    );
  }
}

/**
 * QN-002 Rising Queen: promote the most-advanced remaining pawn to Queen.
 * Identical selection logic to PN-006 Sacrifice.
 * Mutates state in place (called on deep-cloned state).
 */
function applyRisingQueenPromotion(state: ChessState, color: Color): void {
  const remainingPawns = state.pieces.filter(
    (p) => p.type === "PAWN" && p.color === color,
  );
  if (remainingPawns.length === 0) return;

  const sorted = [...remainingPawns].sort((a, b) => {
    const rankDiff =
      color === "WHITE"
        ? b.position.rank - a.position.rank // WHITE: descending rank
        : a.position.rank - b.position.rank; // BLACK: ascending rank
    if (rankDiff !== 0) return rankDiff;
    return a.position.file.localeCompare(b.position.file);
  });

  const target = sorted[0];
  if (!target) return;
  target.type = "QUEEN";
}

/**
 * Apply a move action to the game state
 * Returns a new state (pure function - does not mutate input)
 */
export function applyMove(state: ChessState, action: MoveAction): ChessState {
  const newState = JSON.parse(JSON.stringify(state)) as ChessState;

  const pieceIndex = newState.pieces.findIndex((p) => p.id === action.pieceId);
  if (pieceIndex === -1) return newState;

  const piece = newState.pieces[pieceIndex];
  if (!piece) return newState;
  const originalPosition = { ...piece.position };

  // Remove piece from old position
  const oldIndices = squareToIndices(action.from);
  newState.board.squares[oldIndices.fileIndex]![oldIndices.rankIndex]!.pieceId =
    null;

  // Save captured piece info before handleCapture removes it (for PN-006 and QN-002 triggers)
  const capturedPieceSnapshot = action.capturedPieceId
    ? (newState.pieces.find((p) => p.id === action.capturedPieceId) ?? null)
    : null;

  // PN-012 Morph Promotion: record captured enemy piece type for the moving player
  if (capturedPieceSnapshot) {
    const capturingColor = piece.color;
    const capturedType = capturedPieceSnapshot.type;
    const capturedTypes = newState.capturedPieceTypes ?? {
      white: [],
      black: [],
    };
    const colorKey = capturingColor === "WHITE" ? "white" : "black";
    if (!capturedTypes[colorKey].includes(capturedType)) {
      newState.capturedPieceTypes = {
        ...capturedTypes,
        [colorKey]: [...capturedTypes[colorKey], capturedType],
      };
    } else {
      newState.capturedPieceTypes = capturedTypes;
    }
  }

  // Handle captures
  const wasCapture = handleCapture(newState, action);
  const wasCaptureOrPawnMove = wasCapture || piece.type === "PAWN";

  // Handle pawn promotion
  if (action.promoteTo && piece.type === "PAWN") {
    if (hasDualKnightPromotion(newState, piece)) {
      // PN-007: override promotion to Knight, then place second Knight adjacent
      piece.type = "KNIGHT";
      addPieceAdjacent(
        newState,
        piece.color,
        action.to,
        "KNIGHT",
        "pn007",
        newState.turnNumber,
      );
    } else if (hasDualMinorPromotion(newState, piece)) {
      // PN-013: override promotion to Bishop, then place Knight adjacent
      piece.type = "BISHOP";
      addPieceAdjacent(
        newState,
        piece.color,
        action.to,
        "KNIGHT",
        "pn013",
        newState.turnNumber,
      );
    } else {
      piece.type = action.promoteTo;
    }
  }

  // Update piece position
  piece.position = { ...action.to };
  piece.hasMoved = true;

  // Place piece on new position
  const newIndices = squareToIndices(action.to);
  newState.board.squares[newIndices.fileIndex]![newIndices.rankIndex]!.pieceId =
    piece.id;

  // KN-004 Assassin: after capturing a Queen with a Knight, mark the Knight as weakened.
  // A weakened Knight causes mutual destruction when it next captures a non-Queen piece.
  if (
    capturedPieceSnapshot?.type === "QUEEN" &&
    piece.type === "KNIGHT" &&
    hasAssassinAug(newState, piece.color)
  ) {
    const colorKey = piece.color === "WHITE" ? "white" : "black";
    const existing = newState.weakenedKnightIds ?? { white: [], black: [] };
    if (!existing[colorKey].includes(action.pieceId)) {
      newState.weakenedKnightIds = {
        ...existing,
        [colorKey]: [...existing[colorKey], action.pieceId],
      };
    }
  }

  // KN-004 Assassin mutual destruction: weakened knight captures non-queen → both removed.
  // The knight has already been moved to action.to; now remove it from the board.
  if (action.isMutualDestruction) {
    const knightIdx = newState.pieces.findIndex((p) => p.id === action.pieceId);
    if (knightIdx !== -1) {
      newState.pieces.splice(knightIdx, 1);
      const landingIndices = squareToIndices(action.to);
      newState.board.squares[landingIndices.fileIndex]![
        landingIndices.rankIndex
      ]!.pieceId = null;
    }
  }

  // Handle castling rook movement
  handleCastlingRookMove(newState, action, piece);

  // Update castling rights
  updateCastlingRights(newState, piece, originalPosition);

  // Update en passant target
  updateEnPassantTarget(newState, action, piece);

  // Update half-move clock
  newState.halfMoveClock = wasCaptureOrPawnMove
    ? 0
    : newState.halfMoveClock + 1;

  // Switch current player
  const movingColor = state.currentPlayer;
  newState.currentPlayer = movingColor === "WHITE" ? "BLACK" : "WHITE";

  // Track consecutive checks
  trackConsecutiveChecks(newState, movingColor);

  // Increment turn number when black finishes their move
  if (movingColor === "BLACK") {
    newState.turnNumber++;

    // Check if a mid-game augmentation selection should be triggered
    const triggerTurn = AUGMENTATION_TRIGGER_TURNS[newState.augmentationRound];
    if (triggerTurn !== undefined && newState.turnNumber === triggerTurn) {
      const choices = action.triggeredAugmentationChoices ?? {
        whiteChoices: getRandomAugmentationChoices(
          AUGMENTATION_CHOICES_PER_ROUND,
        ),
        blackChoices: getRandomAugmentationChoices(
          AUGMENTATION_CHOICES_PER_ROUND,
        ),
      };
      newState.augmentationSelection = {
        whiteChoices: choices.whiteChoices,
        blackChoices: choices.blackChoices,
        whiteSelected: false,
        blackSelected: false,
      };
      newState.phase = "AUGMENTATION_SELECTION";
    }

    // QN-002 Rising Queen: decrement active countdowns and fire when they hit 0
    if (newState.risingQueenCountdown) {
      for (const colorKey of ["white", "black"] as const) {
        const entry: number | "used" | undefined =
          newState.risingQueenCountdown[colorKey];
        if (typeof entry !== "number") continue; // "used" or undefined — skip

        const next: number = entry - 1;
        if (next <= 0) {
          // Countdown expired: promote most-advanced pawn to Queen
          const promoColor = colorKey === "white" ? "WHITE" : "BLACK";
          applyRisingQueenPromotion(newState, promoColor);
          newState.risingQueenCountdown = {
            ...newState.risingQueenCountdown,
            [colorKey]: "used",
          };
        } else {
          newState.risingQueenCountdown = {
            ...newState.risingQueenCountdown,
            [colorKey]: next,
          };
        }
      }
    }

    // MN-001 Revival: decrement active countdowns and fire when they hit 0
    if (newState.minorRevivalCountdown) {
      for (const colorKey of ["white", "black"] as const) {
        const entry:
          | {
              pieceType: Piece["type"];
              targetSquare: Square;
              turnsLeft: number;
            }
          | "used"
          | undefined = newState.minorRevivalCountdown[colorKey];
        if (!entry || entry === "used") continue;

        const next: number = entry.turnsLeft - 1;
        if (next <= 0) {
          // Countdown expired: revive the minor piece
          const reviveColor = colorKey === "white" ? "WHITE" : "BLACK";
          applyMinorRevival(
            newState,
            reviveColor,
            entry.pieceType,
            entry.targetSquare,
            newState.turnNumber,
          );
          newState.minorRevivalCountdown = {
            ...newState.minorRevivalCountdown,
            [colorKey]: "used",
          };
        } else {
          newState.minorRevivalCountdown = {
            ...newState.minorRevivalCountdown,
            [colorKey]: { ...entry, turnsLeft: next },
          };
        }
      }
    }
  }

  // QN-001 Queen's Majesty: permanently record that the original queen is lost.
  // Pawn→Queen promotion does NOT restore this flag.
  if (capturedPieceSnapshot?.type === "QUEEN") {
    const queenOwnerKey =
      capturedPieceSnapshot.color === "WHITE" ? "white" : "black";
    const existing = newState.queenMajestyLost ?? {};
    if (!existing[queenOwnerKey]) {
      newState.queenMajestyLost = { ...existing, [queenOwnerKey]: true };
    }
  }

  // QN-002 Rising Queen: start countdown when the player's Queen is captured.
  // Placed AFTER the decrement step so the countdown is not decremented in the
  // same move that starts it (decrement only fires on subsequent black moves).
  if (capturedPieceSnapshot?.type === "QUEEN") {
    const queenOwner = capturedPieceSnapshot.color;
    const queenOwnerKey = queenOwner === "WHITE" ? "white" : "black";
    const existingEntry = newState.risingQueenCountdown?.[queenOwnerKey];
    // Only trigger if QN-002 is active and the effect has never been used
    if (
      existingEntry === undefined &&
      hasRisingQueenAug(newState, queenOwner)
    ) {
      const delay = getRisingQueenDelay(newState, queenOwner) ?? 10;
      newState.risingQueenCountdown = {
        ...newState.risingQueenCountdown,
        [queenOwnerKey]: delay,
      };
    }
  }

  // MN-001 Revival: start countdown when the first minor piece (Knight/Bishop) is captured.
  // Placed AFTER the decrement step for the same reason as QN-002 above.
  if (
    capturedPieceSnapshot?.type === "KNIGHT" ||
    capturedPieceSnapshot?.type === "BISHOP"
  ) {
    const minorOwner = capturedPieceSnapshot.color;
    const minorOwnerKey = minorOwner === "WHITE" ? "white" : "black";
    const existingEntry = newState.minorRevivalCountdown?.[minorOwnerKey];
    // Only trigger if MN-001 is active and the effect has never been used
    if (
      existingEntry === undefined &&
      hasMinorRevivalAug(newState, minorOwner)
    ) {
      const delay = getMinorRevivalDelay(newState, minorOwner) ?? 5;
      const pieceType = capturedPieceSnapshot.type; // "KNIGHT" or "BISHOP"
      const targetSquare = getMinorRevivalTargetSquare(
        newState,
        minorOwner,
        pieceType,
      );
      newState.minorRevivalCountdown = {
        ...newState.minorRevivalCountdown,
        [minorOwnerKey]: { pieceType, targetSquare, turnsLeft: delay },
      };
    }
  }

  // PN-006 Sacrifice: trigger when a pawn is captured
  if (capturedPieceSnapshot?.type === "PAWN") {
    applySacrificePromotion(newState, capturedPieceSnapshot.color);
  }

  // Record history
  newState.actionHistory.push(action);
  newState.positionHistory.push(getPositionHash(newState));
  newState.updatedAt = new Date().toISOString();

  return newState;
}
